import { useEffect, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { Logo } from '@/components/ui/Logo';

// True when running as home-screen PWA standalone (no Safari chrome).
const isStandalone =
  (window.navigator as { standalone?: boolean }).standalone === true ||
  window.matchMedia('(display-mode: standalone)').matches;

export default function AuthCallbackPage() {
  const history = useHistory();
  const [error, setError] = useState<string | null>(null);
  // True when the callback opened in Safari (not in the PWA web view)
  const [openedInBrowser, setOpenedInBrowser] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('handle')
          .eq('id', session.user.id)
          .single();

        sub.subscription.unsubscribe();

        // If this callback opened in Safari (not standalone), we can't redirect into
        // the PWA — tell the user to return to the app on their home screen.
        if (!isStandalone) {
          setOpenedInBrowser(true);
          return;
        }

        if (!profile || profile.handle === null) {
          history.replace('/auth/claim-handle');
        } else {
          history.replace('/');
        }
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        sub.subscription.unsubscribe();
        if (isStandalone) history.replace('/');
      }
    });

    const timeout = setTimeout(() => {
      setError('The sign-in link may have expired. Please request a new one.');
    }, 8000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [history]);

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="flex min-h-full flex-col items-center justify-center bg-[#faf9f7] px-6 py-16">
          <div className="flex flex-col items-center gap-6 text-center">
            <Logo size="md" />

            {openedInBrowser ? (
              /* Opened in Safari from email link — guide user back to the PWA */
              <div className="max-w-xs">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5">
                    <path d="M20 6 9 17l-5-5"/>
                  </svg>
                </div>
                <h2 className="mb-2 font-serif text-xl font-bold text-gray-900">You're signed in!</h2>
                <p className="mb-6 text-sm text-gray-500 leading-relaxed">
                  Return to the <strong>Open iPub</strong> app on your home screen to continue reading.
                </p>
                {/* Fallback: open in same browser tab — works on desktop or when not using home-screen PWA */}
                <a
                  href="/"
                  className="inline-block rounded-lg bg-[#2f3e46] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33]"
                >
                  Continue in browser
                </a>
              </div>
            ) : error ? (
              <div>
                <p className="mb-4 text-sm text-red-600">{error}</p>
                <a href="/auth" className="text-sm text-[#1a56a0] hover:underline">
                  Back to sign in
                </a>
              </div>
            ) : (
              <>
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
                <p className="text-sm text-gray-500">Signing you in…</p>
              </>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
